/**
 * Общий редактор для CashReceipt (ПКО) и CashPayment (РКО).
 * Структура зеркальная BankDocEditor, но с полями кассы:
 * cash_register_id, received_from/issued_to, basis, employee_id.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cashReceipts, cashPayments,
  type CashReceiptCreate, type CashPaymentCreate,
  type ReceiptOperationType, type PaymentOperationType,
} from '@/api/cashDocs';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import { employees as employeesApi } from '@/api/hr';
import { refs } from '@/api/refs';
import { CFI_BY_OP } from '@/shared/utils/cfiByOp';
import { customerOrders, supplierOrders } from '@/api/orders';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import AccountSelect from '@/components/AccountSelect';
import QuickCreateLink from '@/shared/components/QuickCreateLink';
import QuickContractModal from '@/shared/components/QuickContractModal';
import QuickContractorModal from '@/shared/components/QuickContractorModal';
import { accounts as accountsApi } from '@/api/documents';
import { ProjectField } from '@/components/ProjectSelect';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { contractLabel } from '@/shared/utils/labels';
import { numberSchemes } from '@/api/numberSchemes';
import { createThenPost } from '@/shared/utils/createThenPost';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import { useAdvanceInvoiceAction } from '@/shared/hooks/useAdvanceInvoiceAction';
import { vatPayerOn } from '@/shared/utils/vatFlags';

interface Props {
  kind: 'receipt' | 'payment';
}

const RECEIPT_OPS: ReceiptOperationType[] = ['FROM_BUYER', 'FROM_BANK', 'FROM_PODOTCHET', 'LOAN', 'LOAN_GIVEN_RETURN', 'TRANSIT_IN', 'OTHER'];
const PAYMENT_OPS: PaymentOperationType[] = [
  'TO_SUPPLIER', 'TO_BANK', 'TO_PODOTCHET', 'SALARY', 'DEPOSIT_PAYOUT', 'LOAN_RETURN', 'LOAN_GIVEN', 'TRANSIT_OUT', 'OTHER',
];

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  cash_register_id: string;
  bank_account_id: string;   // ч.1349: «з банку» / «в банк»
  operation_type: string;
  cash_flow_item_id: string;  // ч.1364: стаття ДДС (TD-PAGE-AUDIT #110) — авто за видом операції
  contractor_id: string;
  contract_id: string;
  order_id: string;  // П4a: «сделка» — замовлення/рахунок (deal розрахунків)
  employee_id: string;  // для SALARY / підзвіт (резолв індивід. рах. розрахунків)
  amount: string;
  // ч.1446: ставка і сума ПДВ надходження від покупця — з них ПДВ авансу (Дт 6431 — Кт 6432).
  vat_rate_id: string;
  vat_amount: string;
  party_text: string;   // received_from или issued_to
  basis: string;
  corresponding_account: string;
  comment: string;
  project_id: string | null;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  cash_register_id: '',
  bank_account_id: '',
  operation_type: '',
  cash_flow_item_id: '',
  contractor_id: '',
  contract_id: '',
  order_id: '',
  employee_id: '',
  amount: '0',
  vat_rate_id: '',
  vat_amount: '0',
  party_text: '',
  basis: '',
  corresponding_account: '',
  comment: '',
  project_id: null,
};

export default function CashDocEditor({ kind }: Props) {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  // обе API одинаковой формы; даём типу свободу пройти через мутации
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const api: any = kind === 'receipt' ? cashReceipts : cashPayments;
  const listKey = kind === 'receipt' ? 'cash-receipts' : 'cash-payments';
  const itemKey = kind === 'receipt' ? 'cash-receipt' : 'cash-payment';
  const baseRoute = kind === 'receipt' ? '/cash-receipts' : '/cash-payments';
  const opOptions = kind === 'receipt' ? RECEIPT_OPS : PAYMENT_OPS;
  const defaultOp = kind === 'receipt' ? 'FROM_BUYER' : 'TO_SUPPLIER';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX permissions-by-org α: фільтр org dropdown по per-org permission.
  // Без цього user бачив усі orgs accessible через user_access, але save
  // міг впасти на 403 (per-org permission відсутній). Тепер dropdown
  // показує лише ті org, де реально можна створити ПКО/РКО.
  const requiredPerm = kind === 'receipt' ? 'cash.pko.manage' : 'cash.rko.manage';
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg(requiredPerm);
  const cList = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const docQuery = useQuery({
    queryKey: [itemKey, id],
    queryFn: () => api.get(id!),
    enabled: !isNew,
  });
  // ч.1267: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, itemKey, (cid) => api.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY);
  // ч.1364: стаття ДДС — авто за видом операції (як у банку, ч.752), юзер може змінити;
  // перезаписуємо лише порожню або попередньо авто-підставлену.
  const cfiQ = useQuery({ queryKey: ['cash-flow-items'], queryFn: refs.cashFlowItems, staleTime: 300_000 });
  // ч.1446: ставки ПДВ надходження; сума ПДВ — авто зі ставки (як у банку, ч.753), ставка за
  // замовчуванням 20% у новому надходженні від покупця.
  const vatQ = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates, staleTime: 300_000,
    enabled: kind === 'receipt' });
  const cfiAutoRef = useRef<string>('');
  useEffect(() => {
    if (!cfiQ.data) return;
    const code = CFI_BY_OP[form.operation_type];
    const auto = code ? cfiQ.data.find((x) => x.is_system && x.code === code)?.id ?? '' : '';
    if (!auto) return;
    if (!form.cash_flow_item_id || form.cash_flow_item_id === cfiAutoRef.current) {
      cfiAutoRef.current = auto;
      if (form.cash_flow_item_id !== auto) setForm((f) => ({ ...f, cash_flow_item_id: auto }));
    }
  }, [form.operation_type, cfiQ.data, form.cash_flow_item_id]);
  const cfiItems = useMemo(
    () => (cfiQ.data ?? []).map((x) => ({ id: x.id, label: x.name })), [cfiQ.data]);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [error, setError] = useState<string | null>(null);

  // ч.944 (баг власника): автонумерація ПКО/РКО — превʼю наступного номера
  // (той самий механізм, що проставляє номер при збереженні: number_service).
  const docTypeForNumber = kind === 'receipt' ? 'doc.cash_receipts' : 'doc.cash_payments';
  const [numberTouched, setNumberTouched] = useState(false);
  const nextNumberQ = useQuery({
    queryKey: ['cash-next-number', docTypeForNumber, form.organization_id],
    queryFn: () => numberSchemes.preview({
      document_type: docTypeForNumber,
      organization_id: form.organization_id || undefined,
    }),
    enabled: isNew && !!form.organization_id,
  });
  useEffect(() => {
    if (isNew && !numberTouched && nextNumberQ.data?.preview && !form.document_number) {
      setForm((f) => (f.document_number ? f : { ...f, document_number: nextNumberQ.data!.preview }));
    }
  }, [isNew, numberTouched, nextNumberQ.data, form.document_number]);

  // ч.947 (зауваження власника): у полі «Кореспондуючий рахунок» показуємо
  // НОМЕР рахунку, який підставиться автоматично (дзеркало cash_posting:
  // RECEIPT_CORRESPONDING / PAYMENT_CORRESPONDING), а не слово «авто».
  const AUTO_ACCOUNT_BY_OP: Record<string, string> = {
    FROM_BUYER: '361', FROM_BANK: '311', FROM_PODOTCHET: '372',
    LOAN: '6851', LOAN_GIVEN_RETURN: '3771', TRANSIT_IN: '6851',
    TO_SUPPLIER: '631', TO_BANK: '311', TO_PODOTCHET: '372', SALARY: '661',
    DEPOSIT_PAYOUT: '662', LOAN_RETURN: '6851', LOAN_GIVEN: '3771',
    TRANSIT_OUT: '6851', OTHER: '6851',
  };
  const autoAccountCode = AUTO_ACCOUNT_BY_OP[form.operation_type] ?? '6851';

  // ч.945/ч.947: швидке створення контрагента і договору просто з форми
  // (власник: попапи ПОТРІБНІ, зауваження було лише про вигляд посилання).
  const [quickContractor, setQuickContractor] = useState(false);
  const [quickContract, setQuickContract] = useState(false);

  // ч.946: план рахунків — щоб показувати НОМЕР рахунку у вкладці «Рахунки
  // обліку» (у документі зберігається код, AccountSelect працює з id).
  const accountsQ = useQuery({
    queryKey: ['accounts', 'all'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 600 }),
    staleTime: 60 * 60 * 1000,
  });
  const accIdByCode = useMemo(
    () => new Map((accountsQ.data ?? []).map((a) => [a.code, a.id] as const)),
    [accountsQ.data]);
  const accCodeById = useMemo(
    () => new Map((accountsQ.data ?? []).map((a) => [a.id, a.code] as const)),
    [accountsQ.data]);

  const crQ = useQuery({
    queryKey: ['cash-registers', form.organization_id],
    queryFn: () => refs.cashRegisters(form.organization_id),
    enabled: !!form.organization_id,
  });
  // Операції з працівником — для них показуємо вибір співробітника
  // (резолв індивід. рах. розрахунків при виплаті ЗП / підзвіт).
  const EMP_OPS = ['SALARY', 'DEPOSIT_PAYOUT', 'TO_PODOTCHET', 'FROM_PODOTCHET'];
  const showEmployee = EMP_OPS.includes(form.operation_type);
  // ч.944 (зауваження власника): операції БЕЗ контрагента і договору —
  // рух між власною касою і банком (зняття/внесення) та операції з
  // ПРАЦІВНИКОМ (підзвіт, зарплата): сторона там не контрагент.
  const NO_PARTY_OPS = ['FROM_BANK', 'TO_BANK', ...EMP_OPS];
  const showContractor = !NO_PARTY_OPS.includes(form.operation_type);
  // ч.1349: зняття / внесення готівки — другий бік проводки 311 потребує рахунку банку
  // (аналітика ОСВ по 311 і зведення банку); за замовчуванням — основний рахунок.
  const isBankOp = form.operation_type === 'FROM_BANK' || form.operation_type === 'TO_BANK';
  const bankAccountsQ = useQuery({
    queryKey: ['bank-accounts', form.organization_id],
    queryFn: () => refs.bankAccounts(form.organization_id),
    enabled: !!form.organization_id && isBankOp,
  });
  const _postedDoc = !!docQuery.data?.posted;
  useEffect(() => {
    if (!isBankOp || _postedDoc || form.bank_account_id || !bankAccountsQ.data?.length) return;
    const pick = bankAccountsQ.data.find((a) => a.is_main) ?? bankAccountsQ.data[0]!;
    setForm((f) => ({ ...f, bank_account_id: pick.id }));
  }, [isBankOp, _postedDoc, form.bank_account_id, bankAccountsQ.data]);
  const empQ = useQuery({
    queryKey: ['cash-employees', form.organization_id],
    queryFn: () => employeesApi.list({
      organization_id: form.organization_id, active_only: true, limit: 500,
    }),
    enabled: !!form.organization_id && showEmployee,
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });
  // П4a: замовлення/рахунки контрагента для поля «На підставі» (deal).
  const showOrderField = kind === 'receipt'
    ? form.operation_type === 'FROM_BUYER'
    : form.operation_type === 'TO_SUPPLIER';
  const ordersApi = kind === 'receipt' ? customerOrders : supplierOrders;
  const dealOrdersQ = useQuery({
    queryKey: ['deal-orders', kind, form.organization_id, form.contractor_id],
    queryFn: () => ordersApi.list({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
      posted: true, limit: 100,
    }),
    enabled: !!form.organization_id && !!form.contractor_id && showOrderField,
  });

  // ч.740: «створити на підставі» з документів Покупок — query-параметри.
  const [sp] = useSearchParams();
  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: sp.get('org') || firstOrg,
        operation_type: sp.get('op') || f.operation_type || defaultOp,
        contractor_id: sp.get('contractor') || f.contractor_id,
        contract_id: sp.get('contract') || f.contract_id,
        order_id: sp.get('order') || f.order_id,
        amount: sp.get('amount') || f.amount,
        basis: sp.get('purpose') || f.basis,
        project_id: sp.get('project') || f.project_id,
        corresponding_account: sp.get('corr') || f.corresponding_account,
        // ч.882: «виплатити депонент» з депонування ЗП (РКО DEPOSIT_PAYOUT по працівнику).
        employee_id: sp.get('employee') || f.employee_id,
        party_text: sp.get('party') || f.party_text,
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        cash_register_id: d.cash_register_id,
        bank_account_id: d.bank_account_id ?? '',
        operation_type: d.operation_type,
        cash_flow_item_id: (d as { cash_flow_item_id?: string | null }).cash_flow_item_id ?? '',
        contractor_id: d.contractor_id ?? '',
        contract_id: d.contract_id ?? '',
        order_id: (kind === 'receipt'
          ? d.customer_order_id : d.supplier_order_id) ?? '',
        employee_id: d.employee_id ?? '',
        amount: String(d.amount),
        vat_rate_id: (d as { vat_rate_id?: string | null }).vat_rate_id ?? '',
        vat_amount: String((d as { vat_amount?: string | number | null }).vat_amount ?? '0'),
        party_text: (kind === 'receipt' ? d.received_from : d.issued_to) ?? '',
        basis: d.basis ?? '',
        corresponding_account: (d as { corresponding_account?: string | null })
          .corresponding_account ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка до замовлень і підстав
        setForm((f) => ({ ...f, document_number: EMPTY.document_number, document_date: nowForDocDate(), order_id: EMPTY.order_id, }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs, defaultOp, kind]);

  // подставить первую кассу
  useEffect(() => {
    if (isNew && !form.cash_register_id && crQ.data && crQ.data.length > 0) {
      const first = crQ.data[0];
      if (first) setForm((f) => ({ ...f, cash_register_id: first.id }));
    }
  }, [isNew, crQ.data, form.cash_register_id]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  const vatAutoRef = useRef<string>('');
  useEffect(() => {
    if (kind !== 'receipt' || isPosted) return;
    const rate = (vatQ.data ?? []).find((v) => v.id === form.vat_rate_id)?.rate_percent;
    const amt = Number(form.amount);
    if (rate === undefined || !(amt > 0)) return;
    const auto = (amt * Number(rate) / (100 + Number(rate))).toFixed(2);
    const cur = form.vat_amount;
    if (!cur || Number(cur) === 0 || cur === vatAutoRef.current) {
      vatAutoRef.current = auto;
      if (cur !== auto) setForm((f) => ({ ...f, vat_amount: auto }));
    }
  }, [kind, isPosted, form.amount, form.vat_rate_id, vatQ.data, form.vat_amount]);
  useEffect(() => {
    if (kind !== 'receipt' || !isNew || form.vat_rate_id || !vatQ.data
        || form.operation_type !== 'FROM_BUYER') return;
    const r20 = vatQ.data.find((v) => Number(v.rate_percent) === 20 && v.is_active !== false);
    if (r20) setForm((f) => (f.vat_rate_id ? f : { ...f, vat_rate_id: r20.id }));
  }, [kind, isNew, vatQ.data, form.vat_rate_id, form.operation_type]);
  // ч.946 (канон вкладок, як у платіжці): блок 3 — дві вкладки.
  const [docTab, setDocTab] = useState<'party' | 'accounts'>('party');

  const buildPayload = () => {
    const partyKey = kind === 'receipt' ? 'received_from' : 'issued_to';
    return {
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      cash_register_id: form.cash_register_id,
      bank_account_id: isBankOp ? (form.bank_account_id || null) : null,
      operation_type: form.operation_type as ReceiptOperationType & PaymentOperationType,
      cash_flow_item_id: form.cash_flow_item_id || null,
      contractor_id: form.contractor_id || null,
      contract_id: form.contract_id || null,
      // П4a: «сделка» — розрахунки у розрізі замовлень.
      ...(kind === 'receipt'
        ? { customer_order_id: form.order_id || null }
        : { supplier_order_id: form.order_id || null }),
      employee_id: form.employee_id || null,
      amount: form.amount,
      ...(kind === 'receipt'
        ? { vat_rate_id: form.vat_rate_id || null, vat_amount: form.vat_amount || '0' }
        : {}),
      [partyKey]: form.party_text || null,
      basis: form.basis || null,
      corresponding_account: form.corresponding_account || null,
      comment: form.comment || null,
      project_id: form.project_id,
    };
  };

  const create = useMutation({
    mutationFn: () => api.create(buildPayload() as CashReceiptCreate & CashPaymentCreate),
    onSuccess: (doc: { id: string }) => {
      qc.invalidateQueries({ queryKey: [listKey] });
      navigate(`${baseRoute}/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => api.update(id!, buildPayload()),
    onSuccess: () => qc.invalidateQueries({ queryKey: [itemKey, id] }),
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    // ч.1266: «провести» на новому документі — створити, провести, відкрити створений;
    // на наявному — спершу зберегти правки форми (як у банку з ч.837), потім провести.
    mutationFn: async () => {
      if (isNew) {
        return createThenPost(
          () => api.create(buildPayload() as CashReceiptCreate & CashPaymentCreate) as Promise<{ id: string }>,
          (docId) => api.post(docId),
          (doc) => { qc.invalidateQueries({ queryKey: [listKey] }); navigate(`${baseRoute}/${doc.id}`); },
        );
      }
      if (!isPosted) await api.update(id!, buildPayload());
      return api.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [itemKey, id] });
      qc.invalidateQueries({ queryKey: [listKey] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => api.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [itemKey, id] });
      qc.invalidateQueries({ queryKey: [listKey] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => api.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [listKey] });
      navigate(baseRoute);
    },
  });

  const titleNew = kind === 'receipt' ? t('cr.new') : t('cp.new');
  const titleList = kind === 'receipt' ? t('cr.title') : t('cp.title');

  // ч.1447 (рішення власника): «створити ПН на аванс» — і з ПКО, як у 1С: підстава — сам ордер,
  // рядки — з рахунку, якщо його вказано; ПДВ з авансу нараховує ордер, ПН переносить на 6412.
  const docOrg = (orgList.data?.items ?? []).find((o) => o.id === form.organization_id);
  const { action: advAction } = useAdvanceInvoiceAction({
    receiptType: 'doc.cash_receipts', receiptId: id, organizationId: form.organization_id,
    applicable: kind === 'receipt' && form.operation_type === 'FROM_BUYER', isNew,
    version: docQuery.dataUpdatedAt,
    blockedReason: docOrg && !vatPayerOn(docOrg, form.document_date)
      ? 'Організація не є платником ПДВ — податкові накладні не складаються' : null,
  });

  // UX α: warning якщо новий документ а user не має permission у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{titleNew}</h2>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати {kind === 'receipt' ? 'ПКО' : 'РКО'}{' '}
            у жодній з ваших організацій ({totalAccessibleOrgs}).
            Зверніться до адміністратора tenant'а.
          </div>
          <button onClick={() => navigate(baseRoute)}>{t('common.cancel')}</button>
        </div>
      </div>
    );
  }

  // ч.868 (новий дизайн, Закон #10): права колонка ордера. Прогрес — лише обовʼязкові (схема:
  // організація, дата, каса, тип операції, сума > 0). ч.1447: у ПКО від покупця — «ПН на аванс».
  const docType = kind === 'receipt' ? 'doc.cash_receipts' : 'doc.cash_payments';
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] =
    advAction ? [advAction] : [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'cash', title: 'Каса і операція', required: 3,
      filled: (form.cash_register_id ? 1 : 0) + (form.operation_type ? 1 : 0)
        + (Number(form.amount) > 0 ? 1 : 0) },
    { id: 'party', title: 'Контрагент і підстава', required: 0, filled: 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? titleNew : `${titleList}: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate(baseRoute)}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => post.mutate()}
          postPending={post.isPending}
          postDisabled={!(Number(form.amount) > 0)}
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          deleteTitle="Помилкове введення — приховати з типового списку"
          onPrint={() => navigate(`${baseRoute}/${id}/print`)}
          // ч.868: дт-кт — лише у статус-рядку тулбара (окрема DtKtButton прибрана).
          entriesDoc={!isNew && id ? { type: docType, id } : undefined}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`${baseRoute}/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: docType, id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{t('cd.organization')} *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value, cash_register_id: '' })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{t('cd.number')}</label>
          <input value={form.document_number}
                 onChange={(e) => { setNumberTouched(true); setForm({ ...form, document_number: e.target.value }); }}
                 disabled={isPosted}
                 placeholder={isNew ? 'авто' : undefined} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{t('cd.date')} *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
        <ProjectField span={3}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Каса і операція">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>{t('cd.cash_register')} *</label>
          <select value={form.cash_register_id}
                  onChange={(e) => setForm({ ...form, cash_register_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {crQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{c.name} — {c.code}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>{t('cd.operation_type')} *</label>
          <select value={form.operation_type}
                  onChange={(e) => setForm({ ...form, operation_type: e.target.value })}
                  disabled={isPosted}>
            {opOptions.map((op) => (
              <option key={op} value={op}>{t(`cd.ops.${op}`)}</option>
            ))}
          </select>
        </div>
        {isBankOp && (
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Банківський рахунок *</label>
            <select value={form.bank_account_id} aria-label="Банківський рахунок"
                    onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })}
                    disabled={isPosted}>
              <option value="">—</option>
              {bankAccountsQ.data?.map((a) => (
                <option key={a.id} value={a.id}>{a.account_name || a.iban}</option>
              ))}
            </select>
          </div>
        )}
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label title="Стаття руху грошових коштів (зведення «Фінанси», звіт «Рух коштів») — авто за видом операції, можна змінити.">
            Стаття ДДС
          </label>
          <SearchSelect items={cfiItems} value={form.cash_flow_item_id}
            onChange={(id) => setForm({ ...form, cash_flow_item_id: id })} disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>{t('cd.amount')} *</label>
          <input type="number" step="0.01" value={form.amount}
                 onChange={(e) => setForm({ ...form, amount: e.target.value })}
                 disabled={isPosted} />
        </div>
      </div>
      {/* ч.1446: ставка і сума ПДВ — як у ПКО 1С; з авансової частини надходження проводка
          Дт 6431 — Кт 6432 (для «перша подія»). */}
      {kind === 'receipt' && form.operation_type === 'FROM_BUYER' && (
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Ставка ПДВ</label>
            <select value={form.vat_rate_id} disabled={isPosted}
              onChange={(e) => setForm({ ...form, vat_rate_id: e.target.value })}>
              <option value="">без ПДВ</option>
              {(vatQ.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label title="ПДВ у сумі надходження: сума × ставка / (100 + ставка); можна виправити.">Сума ПДВ</label>
            <input type="number" step="0.01" value={form.vat_amount} disabled={isPosted}
              onChange={(e) => setForm({ ...form, vat_amount: e.target.value })} />
          </div>
        </div>
      )}
      </DocBlock>

      <DocBlock index={3} title="Контрагент і підстава">
      {/* 2026-08-09 (рішення власника): вкладка «Рахунки обліку» на всіх
          документах з проводками. Порожнє поле = авто. ч.946: ДВІ вкладки —
          як у платіжці (канон), дт-кт — у статус-рядку тулбара. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs">
          <button type="button" className={docTab === 'party' ? 'active' : ''}
            onClick={() => setDocTab('party')}>Контрагент і підстава</button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={docTab === 'accounts' ? 'active' : ''}
            onClick={() => setDocTab('accounts')}>Рахунки обліку</button>
        </div>
      </div>
      {docTab === 'accounts' && (
        <div className="field" style={{ maxWidth: 420 }}>
          <label title="Кореспондуючий рахунок проводки (порожньо — авто за типом операції: 361/631/661/372/…)">
            Кореспондуючий рахунок</label>
          {/* ч.946 (зауваження власника): вибір рахунку зі списку плану —
              видно НОМЕР і назву (був текстовий input без підказки). */}
          <AccountSelect
            value={accIdByCode.get(form.corresponding_account) ?? ''}
            onChange={(id) => setForm({
              ...form,
              corresponding_account: id ? (accCodeById.get(id) ?? '') : '',
            })}
            placeholder={`${autoAccountCode} (авто за типом операції)`} />
          <small style={{ color: 'var(--muted)' }}>
            Порожньо — проводка піде на <b>{autoAccountCode}</b>
            {form.operation_type === 'LOAN' || form.operation_type === 'LOAN_RETURN'
              ? ' (для кредиту банку — 601)' : ''}
            {form.operation_type === 'FROM_BUYER' ? ' (нерезидент — 362)' : ''}
            {form.operation_type === 'TO_SUPPLIER' ? ' (нерезидент — 632)' : ''}
            .
          </small>
          {form.corresponding_account && !accIdByCode.has(form.corresponding_account) && (
            <small style={{ color: 'var(--badge-amber-fg)' }}>
              Рахунок {form.corresponding_account} не знайдено у плані — перевірте.
            </small>
          )}
        </div>
      )}
      {docTab === 'party' && (<>
      <div className="doc-grid">
        {/* ч.944: для зняття з банку / внесення у банк і операцій з працівником
            контрагент і договір не потрібні — поля не показуємо. */}
        {showContractor && (
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>
            {t('cd.contractor')}
            {/* ч.947/ч.948: «створити ↗» відкриває попап (спільний компонент). */}
            <QuickCreateLink disabled={isPosted} title="Створити нового контрагента"
              onClick={() => setQuickContractor(true)} />
          </label>
          <SearchSelect
            items={((cList.data?.items ?? [])).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={form.contractor_id}
            onChange={(id) => setForm({ ...form, contractor_id: id, contract_id: '' })}
            disabled={isPosted}
          />
        </div>
        )}
        {showContractor && (
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>
            {t('cd.contract')}
            {/* ч.947/ч.948: той самий попап договору, що на імпорті виписки. */}
            <QuickCreateLink disabled={isPosted || !form.contractor_id}
              title={form.contractor_id ? 'Створити договір цього контрагента' : 'Спершу оберіть контрагента'}
              onClick={() => setQuickContract(true)} />
          </label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.contractor_id}>
            <option value="">—</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{contractLabel(c)}</option>
            ))}
          </select>
        </div>
        )}
        {showContractor && showOrderField && !!form.contractor_id && (
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>На підставі замовлення/рахунку
              {' '}<small style={{ color: 'var(--muted)' }}>(необов'язково)</small>
            </label>
            <select value={form.order_id}
                    onChange={(e) => setForm({ ...form, order_id: e.target.value })}
                    disabled={isPosted}
                    title="Оплата зарахується за цим замовленням — розрахунки у розрізі замовлень">
              <option value="">— (без посилання)</option>
              {(dealOrdersQ.data?.items ?? [])
                .filter((o: any) => (o.closing_status ?? 'OPEN') !== 'CLOSED' && o.state !== 'CANCELLED')
                .map((o: any) => (
                  <option key={o.id} value={o.id}>
                    {o.order_kind === 'PROFORMA_INVOICE' ? 'Рахунок' : 'Замовлення'}{' '}
                    {o.document_number} від {new Date(o.document_date).toLocaleDateString('uk-UA')}
                    {' · '}{Number(o.total_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })} грн
                  </option>
                ))}
            </select>
          </div>
        )}
        {showEmployee && (
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Працівник</label>
            <select value={form.employee_id}
                    onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                    disabled={isPosted}
                    title="Для виплати ЗП — індивідуальний рахунок розрахунків працівника підставиться у проводку (правило/картка/661).">
              <option value="">—</option>
              {empQ.data?.items.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.full_name ?? e.code ?? e.id}{e.code && e.full_name ? ` (${e.code})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="field">
        <label>{t('cd.basis')}</label>
        <input value={form.basis}
               onChange={(e) => setForm({ ...form, basis: e.target.value })}
               disabled={isPosted} />
      </div>
      </>)}
      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        {/* ч.868: поле коментаря — state/payload були, input у формі не було. */}
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType={docType} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}
      {/* ч.945/ч.947: попапи швидкого створення просто з форми документа. */}
      {quickContractor && (
        <QuickContractorModal
          direction={kind === 'receipt' ? 'IN' : 'OUT'}
          onClose={() => setQuickContractor(false)}
          onCreated={(cid) => {
            setQuickContractor(false);
            setForm((f) => ({ ...f, contractor_id: cid, contract_id: '' }));
            qc.invalidateQueries({ queryKey: ['contractors'] });
          }} />
      )}
      {quickContract && form.contractor_id && (
        <QuickContractModal
          orgId={form.organization_id}
          contractorId={form.contractor_id}
          contractorName={(cList.data?.items ?? []).find((c) => c.id === form.contractor_id)?.name ?? ''}
          direction={kind === 'receipt' ? 'IN' : 'OUT'}
          operationType={form.operation_type}
          onClose={() => setQuickContract(false)}
          onCreated={(cid) => {
            setQuickContract(false);
            setForm((f) => ({ ...f, contract_id: cid }));
            qc.invalidateQueries({ queryKey: ['contracts', form.organization_id, form.contractor_id] });
          }} />
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
